import { useState } from 'react';
import { Appointment, RestoreResult, ApiError, restoreTimeOffAppointments } from '../api';
import { formatSalon } from '../lib/datetime';
import { Loader2, X, RotateCcw, AlertCircle } from 'lucide-react';

interface TimeOffRestoreDialogProps {
  open: boolean;
  /** Bookings the lifted (or shortened) block had cancelled. */
  restorable: Appointment[];
  /** Called once the dialog closes, with true if anything came back. */
  onClose: (restoredAny: boolean) => void;
}

/**
 * Offered after time off is deleted or shortened.
 *
 * Deleting a block gives the owner their *slots* back but not their *customers*:
 * the bookings it cancelled stay cancelled, and once the dialog that listed them
 * is gone there's no record of who they were. This is the way back.
 *
 * Restoring is not guaranteed. The slot was public for the whole time it sat
 * cancelled, so anything the server refuses comes back in `skipped` with a
 * reason — never silently dropped, because the owner may still want to phone
 * that customer.
 *
 * Only future bookings are ever offered, and everything comes back confirmed.
 */
export default function TimeOffRestoreDialog({
  open,
  restorable,
  onClose,
}: TimeOffRestoreDialogProps) {
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<RestoreResult | null>(null);

  if (!open) return null;

  const close = () => {
    const restoredAny = (result?.restored.length ?? 0) > 0;
    setError('');
    setResult(null);
    onClose(restoredAny);
  };

  const handleRestore = async () => {
    setRestoring(true);
    setError('');
    try {
      setResult(await restoreTimeOffAppointments({ appointmentIds: restorable.map((a) => a.id) }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not restore these bookings.');
    }
    setRestoring(false);
  };

  const count = restorable.length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="animate-in fade-in zoom-in bg-surface flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-xl duration-200">
        <div className="border-subtle flex items-start justify-between gap-3 border-b px-6 py-4">
          <div className="flex gap-3">
            <span className="bg-accent-soft mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
              <RotateCcw className="text-accent h-4 w-4" />
            </span>
            <div>
              <h3 className="text-fg text-lg leading-tight font-bold">
                {result
                  ? 'Bookings restored'
                  : `Bring back ${count} cancelled booking${count === 1 ? '' : 's'}?`}
              </h3>
              {!result && (
                <p className="text-fg-secondary mt-1 text-sm">
                  You cancelled {count === 1 ? 'this booking' : 'these bookings'} for the time off
                  you just removed. The slots are free again — restore {count === 1 ? 'it' : 'them'}{' '}
                  if the {count === 1 ? 'customer is' : 'customers are'} still coming.
                </p>
              )}
            </div>
          </div>
          <button
            onClick={close}
            disabled={restoring}
            className="hover:bg-muted rounded-full p-2 transition-colors disabled:pointer-events-none disabled:opacity-40"
          >
            <X className="text-fg-subtle h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {result ? (
            <div className="space-y-3">
              <p className="text-fg-secondary text-sm">
                <strong>{result.restored.length}</strong> booking
                {result.restored.length === 1 ? '' : 's'} back on the calendar.
              </p>
              {result.skipped.length > 0 && (
                <div className="border-warn/30 bg-warn-soft rounded-lg border px-3 py-3">
                  <p className="text-warn text-sm font-semibold">
                    Couldn't bring {result.skipped.length === 1 ? 'this one' : 'these'} back:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {result.skipped.map((s) => (
                      <li key={s.id} className="text-warn flex items-center gap-2 text-sm">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-medium">{s.name}</span>
                        <span className="text-warn">
                          {formatSalon(s.startTime, 'EEE d MMM · h:mm a')}
                        </span>
                        <span className="text-warn ml-auto shrink-0 text-xs">{s.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              <ul className="divide-subtle border-default divide-y rounded-lg border">
                {restorable.map((appointment) => (
                  <li key={appointment.id} className="px-3 py-2.5">
                    <p className="text-fg text-sm font-semibold">
                      {formatSalon(appointment.startTime, 'EEE d MMM')} ·{' '}
                      {formatSalon(appointment.startTime, 'h:mm a')}
                    </p>
                    <p className="text-fg-secondary text-xs">
                      {appointment.serviceName} — {appointment.customer.name}
                      {appointment.staffName ? ` · ${appointment.staffName}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
              <p className="text-fg-muted mt-3 text-xs">
                These come back <strong>confirmed</strong>. The customer was never told they were
                cancelled, so there's nothing to correct — but any slot taken in the meantime can't
                be given back.
              </p>
              {error && (
                <div className="border-danger/25 bg-danger-soft text-danger mt-3 rounded-lg border px-3 py-2 text-sm">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-subtle flex gap-3 border-t px-6 py-4">
          {result ? (
            <button
              onClick={close}
              className="bg-brand-600 hover:bg-brand-700 flex-1 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={close}
                disabled={restoring}
                className="border-default text-fg-secondary hover:bg-canvas flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50"
              >
                Leave cancelled
              </button>
              <button
                onClick={handleRestore}
                disabled={restoring}
                className="bg-brand-600 hover:bg-brand-700 flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {restoring && <Loader2 className="h-4 w-4 animate-spin" />}
                Restore {count} booking{count === 1 ? '' : 's'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
